import { supabase } from '@/integrations/supabase/client';
import { getShopDayLabel } from '@/utils/shopTime';
import { looksLikeUuid } from '@/utils/bayTypeLabels';

interface BayValidationResult {
  valid: boolean;
  warning?: string;
  error?: string;
}

interface BayConflict {
  hasConflict: boolean;
  conflictingTech?: {
    id: string;
    name: string;
  };
}

// Map bay types to required proficiency categories (must match canonical keys from canonicalCategories.json)
const BAY_TYPE_TO_PROFICIENCY: Record<string, string> = {
  // Display format keys
  'Alignment': 'Alignment',
  'Alignment & ADAS': 'Alignment',
  'ADAS': 'ADAS',
  // DB enum format keys
  'alignment': 'Alignment',
  'alignment-adas': 'Alignment',
  'adas': 'ADAS',
  // Wash bays intentionally excluded — no proficiency gate needed
};

/**
 * Validate if a technician has sufficient proficiency for a specialty bay
 */
export const validateBayProficiency = (
  bayType: string,
  techProficiencies: Record<string, number>
): BayValidationResult => {
  const requiredProficiency = BAY_TYPE_TO_PROFICIENCY[bayType];
  
  // General bays (lifts) don't require specific proficiencies
  if (!requiredProficiency) {
    return { valid: true };
  }
  
  const proficiencyLevel = techProficiencies[requiredProficiency] || 0;
  
  // Warn if proficiency is below 50 (still allow assignment for training purposes)
  if (proficiencyLevel < 50) {
    return {
      valid: true,
      warning: `Low proficiency in ${requiredProficiency} (${proficiencyLevel}/100). Tech may need training for this bay.`
    };
  }
  
  // For alignment-adas, also check ADAS proficiency
  if (bayType === 'Alignment & ADAS' || bayType === 'alignment-adas') {
    const adasProficiency = techProficiencies['ADAS'] || 0;
    if (adasProficiency < 50) {
      return {
        valid: true,
        warning: `Low ADAS proficiency (${adasProficiency}/100). Tech may need additional training.`
      };
    }
  }
  
  return { valid: true };
};

/**
 * Check if a bay is already assigned to another technician
 */
export const checkBayConflict = async (
  bayId: string,
  newTechId: string,
  shopId: string
): Promise<BayConflict> => {
  const { data: bay, error } = await supabase
    .from('shop_bays')
    .select('assigned_tech_id')
    .eq('id', bayId)
    .single();
  
  if (error || !bay) {
    return { hasConflict: false };
  }
  
  // No conflict if bay is unassigned or assigned to the same tech
  if (!bay.assigned_tech_id || bay.assigned_tech_id === newTechId) {
    return { hasConflict: false };
  }
  
  // Fetch the conflicting tech's name
  const { data: tech } = await supabase
    .from('technicians')
    .select('id, name')
    .eq('id', bay.assigned_tech_id)
    .single();
  
  return {
    hasConflict: true,
    conflictingTech: tech ? { id: tech.id, name: tech.name } : undefined
  };
};

/**
 * Sync bay assignments bi-directionally between technicians.bay_assignments
 * and shop_bays.assigned_tech_id.
 *
 * R39: Routes through the atomic `sync_tech_bay_assignments` RPC (migration
 * 20260421235000) so all 4 mutating steps (clear old bays, assign new
 * bays, build display-name array, write tech.bay_assignments) happen in a
 * single Postgres transaction. The prior inline implementation was 5
 * sequential client UPDATEs without atomicity — a network failure between
 * steps 2 and 3 left the tech assigned to no bays at all; between 3 and 5
 * left the denormalized display array stale relative to the join table.
 * Both states heal on next page load but in the meantime smart-assign /
 * proficiencyMatcher (which read tech.bay_assignments) could pick the
 * wrong tech. The RPC also performs a server-side role check + cross-shop
 * bay validation that the inline version had no equivalent of.
 */
export const syncBayAssignments = async (
  techId: string,
  bayIds: string[],
  shopId: string
): Promise<{ success: boolean; error?: string; warning?: string }> => {
  try {
    // Boundary validation: the RPC's `p_bay_ids uuid[]` parameter expects
    // bay UUIDs, not display labels. If any caller ever passes a friendly
    // label like '2-Post Lift' (the bug that produced `invalid input value
    // for enum bay_type` historically), drop it here with a structured
    // warning rather than letting Postgres raise mid-transaction. Returning
    // a warning rather than an error keeps the rest of the tech save flow
    // healthy — the form's other writes (hours, days, proficiencies) are
    // already committed by the time syncBayAssignments runs.
    const validBayIds: string[] = [];
    const invalidInputs: string[] = [];
    for (const v of bayIds ?? []) {
      if (looksLikeUuid(v)) validBayIds.push(v);
      else invalidInputs.push(String(v));
    }
    let validationWarning: string | undefined;
    if (invalidInputs.length > 0) {
      validationWarning =
        `Skipped ${invalidInputs.length} non-UUID bay input(s) before save: ` +
        invalidInputs.slice(0, 5).join(', ') +
        (invalidInputs.length > 5 ? '…' : '');
    }

    // The supabase generated types haven't yet been regenerated to include
    // sync_tech_bay_assignments (defined by migration 20260421235000); cast
    // to bypass until the next type-codegen run picks it up.
    const { data, error } = await (supabase as any).rpc('sync_tech_bay_assignments', {
      p_tech_id: techId,
      p_bay_ids: validBayIds,
      p_shop_id: shopId,
    });

    if (error) {
      return { success: false, error: error.message, warning: validationWarning };
    }

    const result = data as unknown as { success: boolean; reason?: string } | null;
    if (!result?.success) {
      return { success: false, error: result?.reason || 'Bay assignment sync failed', warning: validationWarning };
    }

    return { success: true, warning: validationWarning };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// R39: getBayDisplayName helper deleted — its only caller was the inline
// syncBayAssignments above, which now delegates to the atomic RPC
// `sync_tech_bay_assignments` (migration 20260421235000). The display-name
// mapping lives canonically inside that RPC's CASE expression.

/**
 * Check if a bay is available for a given time slot
 * Returns the earliest available start time if the requested slot is occupied
 */
// R8-L-1: shopTimezone is required to avoid UTC-day bugs
export const checkBayTimeSlotAvailable = async (
  bayId: string,
  requestedStartTime: Date,
  durationMinutes: number,
  shopId: string,
  shopTimezone: string,
  excludeTicketId?: string
): Promise<{
  available: boolean;
  conflictingTicket?: {
    id: string;
    customerName: string;
    optiFinish: Date;
  };
  earliestAvailableStart?: Date;
}> => {
  const requestedEnd = new Date(requestedStartTime.getTime() + durationMinutes * 60000);

  // R8-L-1: Use shop TZ for scheduled date, never UTC split
  const scheduledDate = getShopDayLabel(requestedStartTime.toISOString(), shopTimezone);
  
  let query = supabase
    .from('tickets')
    .select('id, customer_name, start_time, opti_finish, assigned_tech_id')
    .eq('bay_id', bayId)
    .in('status', ['Active', 'Paused'])
    .eq('parking_lot', false)
    .eq('scheduled_date', scheduledDate)
    .not('start_time', 'is', null)
    .not('opti_finish', 'is', null);

  if (excludeTicketId) {
    query = query.neq('id', excludeTicketId);
  }

  const { data: bayTickets, error } = await query;

  if (error || !bayTickets || bayTickets.length === 0) {
    return { available: true };
  }

  // Check for overlaps
  let latestConflictEnd: Date | null = null;
  let conflictingTicket: { id: string; customerName: string; optiFinish: Date } | undefined;

  for (const ticket of bayTickets) {
    const ticketStart = new Date(ticket.start_time);
    const ticketEnd = new Date(ticket.opti_finish);

    // Check if requested time overlaps with this ticket
    if (requestedStartTime < ticketEnd && requestedEnd > ticketStart) {
      if (!latestConflictEnd || ticketEnd > latestConflictEnd) {
        latestConflictEnd = ticketEnd;
        conflictingTicket = {
          id: ticket.id,
          customerName: ticket.customer_name,
          optiFinish: ticketEnd,
        };
      }
    }
  }

  if (latestConflictEnd) {
    return {
      available: false,
      conflictingTicket,
      earliestAvailableStart: latestConflictEnd,
    };
  }

  return { available: true };
};

/**
 * Get all tickets currently occupying a bay for a given date
 * Useful for displaying bay schedules and detecting conflicts
 */
export const getBayOccupancy = async (
  bayId: string,
  scheduledDate: string,
  shopId: string
): Promise<Array<{
  id: string;
  customerName: string;
  techId: string;
  techName?: string;
  startTime: Date;
  optiFinish: Date;
}>> => {
  const { data: tickets, error } = await supabase
    .from('tickets')
    .select(`
      id, customer_name, assigned_tech_id, start_time, opti_finish,
      technicians!tickets_assigned_tech_id_fkey(name)
    `)
    .eq('bay_id', bayId)
    .eq('scheduled_date', scheduledDate)
    .in('status', ['Active', 'Paused'])
    .eq('parking_lot', false)
    .not('start_time', 'is', null)
    .not('opti_finish', 'is', null)
    .order('start_time', { ascending: true });

  if (error || !tickets) {
    console.error('Error fetching bay occupancy:', error);
    return [];
  }

  return tickets.map(t => ({
    id: t.id,
    customerName: t.customer_name,
    techId: t.assigned_tech_id,
    techName: (t.technicians as any)?.name,
    startTime: new Date(t.start_time),
    optiFinish: new Date(t.opti_finish),
  }));
};
